import React from 'react';
import domtoimage from 'dom-to-image';
import dayjs from 'dayjs';
import { ChartComment } from '@DStypes/chart';
import RoutesPath from '@constants/RoutesPath';
import { PricingPlanNames, PricingPlans } from '@constants/pricing';
import { PageNames } from '@constants/common';
import { ProjectDataFile } from '@DStypes/project';

export function makeId(length = 5) {
  let result = '';
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;

  for (let i = 0; i < length; i++)
    result += characters.charAt(Math.floor(Math.random() * charactersLength));

  return result;
}

interface SaveDomAsImageProps {
  exportName: string;
  format?: 'png' | 'jpg' | 'jpeg';
  domRef: React.MutableRefObject<HTMLElement | undefined>;
  domChartRef: React.MutableRefObject<HTMLElement | undefined>;
  isEditChart?: boolean;
  onCompleteCallback?: () => void;
  mobileView?: boolean;
}

export const saveDomAsImage = async ({
  exportName,
  format = 'png',
  domRef,
  onCompleteCallback,
  mobileView,
  domChartRef
}: SaveDomAsImageProps) => {
  const nodeToPrint: HTMLElement = domRef?.current as HTMLElement;
  const buttonElement = document.getElementById('removeWaterMarkButton');

  if (buttonElement?.style) buttonElement.style.display = 'none';

  const nodeToScale: HTMLElement = domChartRef?.current as HTMLElement;

  if (nodeToPrint) {
    const desiredWidth = 3840;
    const desiredHeight = 2160;

    // Adjust the size of the DOM element to match the desired resolution
    const originalWidth = mobileView
      ? nodeToScale.offsetWidth - 200
      : nodeToPrint.offsetWidth;
    const originalHeight = mobileView
      ? nodeToScale.offsetHeight
      : nodeToPrint.offsetHeight;
    const scaleFactorX =
      desiredWidth /
      (mobileView ? nodeToScale.offsetWidth : nodeToPrint.offsetWidth);
    const scaleFactorY =
      desiredHeight /
      (mobileView ? nodeToScale.offsetWidth : nodeToPrint.offsetHeight);
    const requiredScaleFactor = Math.min(scaleFactorX, scaleFactorY);

    const imgEl = new Image();

    domtoimage.toSvg(nodeToPrint).then(function (imgDataUrl) {
      imgEl.src = imgDataUrl;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const canvasConfig = {
        width: originalWidth * requiredScaleFactor, // Set the desired width of the canvas
        height: originalHeight * requiredScaleFactor
      };

      canvas.width = canvasConfig.width; // Set the desired width of the canvas
      canvas.height = canvasConfig.height;
      (ctx as CanvasRenderingContext2D).fillStyle = 'white';
      (ctx as CanvasRenderingContext2D).fillRect(
        0,
        0,
        canvasConfig.width,
        canvasConfig.height
      );
      imgEl.onload = function () {
        // Draw the image onto the canvas
        (ctx as CanvasRenderingContext2D).drawImage(
          imgEl,
          0,
          0,
          canvasConfig.width,
          canvasConfig.height
        );

        const link = document.createElement('a');

        link.href = canvas.toDataURL();
        link.download = `${exportName}.${format}`;
        link.click();
        link.remove();
        canvas.remove();
        if (onCompleteCallback) onCompleteCallback();
      };
    });
  }
};

export const formatTitle = (title = '', length = 50) => {
  if (title?.length <= length) return title;

  return `${title?.slice(0, length)}...`;
};

export const formatShortDesc = (shortDesc: string) => {
  if (shortDesc.length <= 150) return shortDesc;

  return `${shortDesc.slice(0, 72)}...${shortDesc.slice(-75)}`;
};
export const formatLongDesc = (longDesc: string) => {
  if (longDesc.length <= 500) return longDesc;

  return `${longDesc.slice(0, 247)}...${longDesc.slice(-250)}`;
};

export const getCommentLocalTime = (comment: ChartComment) => {
  let timestamp = dayjs.utc(comment.updated_at, 'YYYY-MM-DD HH:mm:ss');

  timestamp = dayjs(timestamp).tz(dayjs.tz.guess());

  return timestamp.utc().format('- ddd DD MMM YYYY hh:mm A UTC');
};

export const convertToExcelColumnNumber = (number: any) => {
  const baseChar = 'A'.charCodeAt(0);
  let index = '';

  do {
    number = number - 1;
    index = String.fromCharCode(baseChar + (number % 26)) + index;
    number = (number / 26) >> 0;
  } while (number > 0);

  return index;
};

export const formatChartAxisTitle = (title: string, length = 30) => {
  if (title?.length <= length || title.includes('\n')) return title;

  const words = title.split(' ');
  const result = [];

  if (title.split(' ').length === 1) {
    result.push(
      title.substring(0, length),
      title.substring(length, length * 2),
      title.substring(length * 2, title.length)
    );
  } else if (title.split(' ').length === 3) {
    const index = words[0].length + words[1].length + 2;

    result.push(
      words[0] + ' ' + words[1] + ' ' + words[2].substring(0, length - index),
      words[2].substring(index, length + index),
      words[2].substring(length + index, words[2].length)
    );
  }

  if (result.length)
    return `${result[0]}${result[1] ? `\n${result[1]}` : ''}${
      result[2] ? '...' : ''
    }`;

  let currentSentence = '';

  for (let i = 0; i < words.length; i++) {
    const word = words[i];

    if ((currentSentence + ' ' + word).length > length) {
      result.push(currentSentence.trim());

      currentSentence = '';
    }

    if (currentSentence === '') currentSentence += word;
    else currentSentence += ' ' + word;
  }

  result.push(currentSentence.trim());

  if (result.length === 1) return `${result[0]}`;
  if (result.length === 2) return `${result[0]}\n${result[1]}`;
  else return `${result[0]}\n${result[1]}...`;
};

export const roundNumber = (num: number, decimalPlaces = 0): number => {
  if (num < 0) return -roundNumber(-num, decimalPlaces);
  const power = Math.pow(10, decimalPlaces);
  const number = Number((num * power).toPrecision(15));

  return Math.round(number) / power;
};

export const splitIntoChunk = (arr: any[], chunk: number) => {
  const chunkArr = [];

  for (let i = 0; i < arr.length; i += chunk) {
    const tempArray = arr.slice(i, i + chunk);

    chunkArr.push(tempArray);
  }

  return chunkArr;
};

const alphaNumRegex = '([A-Za-z0-9]+(-[A-Za-z0-9]+)+)';

export const getPageName = (customPath = '') => {
  const path = customPath || window.location.pathname;

  if (path.match(RoutesPath.MY_PROJECTS)) return PageNames.MY_PROJECTS;
  else if (path.match(RoutesPath.DELETED_PROJECTS))
    return PageNames.DELETED_PROJECTS;
  else if (path.match(RoutesPath.SIGN_IN)) return PageNames.SIGN_IN;
  else if (path.match(RoutesPath.SIGN_UP)) return PageNames.SIGN_UP;
  else if (path.match(`/project/${alphaNumRegex}/review`))
    return PageNames.PROJECT_REVIEW;
  else if (path.match(`/project/${alphaNumRegex}/edit`))
    return PageNames.PROJECT_BOARD;
  else if (path.match(`/chart/${alphaNumRegex}/edit`))
    return PageNames.EDIT_CHART;
  else if (path.match(`/chart/${alphaNumRegex}/new`))
    return PageNames.ADD_CHART;
  else if (path.match(`/chart/${alphaNumRegex}/${alphaNumRegex}/preview`))
    return PageNames.CHART_PREVIEW;
  else if (path.match(`/project/${alphaNumRegex}/${alphaNumRegex}/preview`))
    return PageNames.PROJECT_BOARD_PREVIEW;
  else if (
    path.match(
      `/chart/${alphaNumRegex}/${alphaNumRegex}/${alphaNumRegex}/preview`
    )
  )
    return PageNames.CHART_PREVIEW;
  else return '';
};

export const downloadBlob = (blob: Blob, name = 'report.pdf') => {
  // Convert your blob into a Blob URL (a special url that points to an object in the browser's memory)
  const blobUrl = URL.createObjectURL(blob);

  // Create a link element
  const link = document.createElement('a');

  // Set link's href to point to the Blob URL
  link.href = blobUrl;
  link.download = name;

  // Append link to the body
  document.body.appendChild(link);

  // Dispatch click event on the link
  // This is necessary as link.click() does not work on the latest firefox
  link.dispatchEvent(
    new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window
    })
  );

  // Remove link from body
  document.body.removeChild(link);
};

export const convertKeyToObject = (key: any, value: any) => {
  const [xAxis, yAxis] = key.split(' & ');

  return {
    xAxis,
    yAxis,
    operation_choices: value?.operation_choices,
    chart_choices: value?.chart_choices,
    settings: value?.settings
  };
};

export const replaceArrayElement = (array: string[]) => {
  const modifiedArray = [];

  for (let i = 0; i < array.length; i++)
    if (array[i] === 'NUMBER')
      modifiedArray.push('NUMBER', 'SCALE', 'PERCENTAGE', 'CURRENCY');
    else if (array[i] === 'OTHER')
      modifiedArray.push('ID', 'EMAIL', 'URL', 'ADDRESS');
    else if (array[i] === 'DATE') modifiedArray.push('DATE', 'DATETIME');
    else modifiedArray.push(array[i]);

  return modifiedArray;
};

export const getAxisType = (axis: string) => {
  switch (axis) {
    case 'EMAIL':
      return 'OTHER';
    case 'ID':
      return 'OTHER';
    case 'URL':
      return 'OTHER';
    case 'ADDRESS':
      return 'OTHER';
    case 'PERCENTAGE':
      return 'NUMBER';
    case 'CURRENCY':
      return 'NUMBER';
    case 'SCALE':
      return 'NUMBER';
    case 'DATETIME':
      return 'DATE';
    default:
      return axis;
  }
};

export const otherDataTypes = ['ID', 'URL', 'ADDRESS', 'EMAIL'];

export const getPossibleCombinations = ({
  xAxis,
  yAxis,
  options,
  isxAxis,
  chartMatrix
}: any) => {
  const disabledOptions: any = [];
  const validOptions: any = [];
  let typesToBeEnabled: any = [];
  let typesToBeEnabledWithFlip: any = [];

  if (isxAxis) {
    const typeOfyAxis = getAxisType(yAxis?.type);

    typesToBeEnabled = chartMatrix
      .map((obj: any) => {
        if (obj.yAxis === typeOfyAxis && obj?.operation_choices)
          return obj.xAxis;
      })
      .filter((value: any) => typeof value !== 'undefined' && value !== 'SELF');

    typesToBeEnabledWithFlip = chartMatrix
      .map((obj: any) => {
        if (obj.xAxis === typeOfyAxis && obj?.operation_choices)
          return obj.yAxis;
      })
      .filter((value: any) => typeof value !== 'undefined' && value !== 'SELF');
  } else {
    const typeOfxAxis = getAxisType(xAxis.type);

    typesToBeEnabled = chartMatrix
      .map((obj: any) => {
        if (obj.xAxis === typeOfxAxis && obj?.operation_choices)
          return obj.yAxis;
      })
      .filter((value: any) => typeof value !== 'undefined' && value !== 'SELF');

    typesToBeEnabledWithFlip = chartMatrix
      .map((obj: any) => {
        if (obj.yAxis === typeOfxAxis && obj?.operation_choices)
          return obj.xAxis;
      })
      .filter((value: any) => typeof value !== 'undefined' && value !== 'SELF');
  }

  typesToBeEnabled = replaceArrayElement(typesToBeEnabled);

  typesToBeEnabledWithFlip = replaceArrayElement(typesToBeEnabledWithFlip);

  if (isxAxis) {
    for (const option of options)
      if (typesToBeEnabled.includes(option.type)) validOptions.push(option);
      else if (typesToBeEnabledWithFlip.includes(option.type))
        validOptions.push({ ...option, toBeFlipped: true });
      else if (
        option.type !== 'DATE' &&
        option.type !== 'DATETIME' &&
        option.value === yAxis?.value
      )
        validOptions.push(option);
      else disabledOptions.push({ ...option, disabled: true });
  } else {
    const modifiedOption = { ...xAxis, label: 'rows' };

    validOptions.push(modifiedOption);
    const newOptions = options.filter(
      (option: any) => option.value !== xAxis?.value
    );

    for (const option of newOptions)
      if (typesToBeEnabled.includes(option.type)) validOptions.push(option);
      else if (typesToBeEnabledWithFlip.includes(option.type))
        validOptions.push({ ...option, toBeFlipped: true });
      else disabledOptions.push({ ...option, disabled: true });
  }

  return {
    normalOptions: typesToBeEnabled,
    allOptions: validOptions.concat(disabledOptions)
  };
};

export const validateEmails = (emails: string[]) => {
  const validEmails = [];
  const emailRegex = /^[\w.+-]+@[\w.-]+\.\w+$/;

  for (const email of emails)
    if (emailRegex.test(email)) validEmails.push(email);

  return validEmails;
};

export const parseJSON = <ResponseType>(
  jsonString: string
): ResponseType | null => {
  try {
    const result = JSON.parse(jsonString);

    return result;
  } catch (error: any) {
    // Handle the JSON parsing error gracefully
    console.error('Error: Failed to parse JSON -', error.message);

    return null;
  }
};

export function convertObjectToArrayOfObjects(obj: any) {
  return Object.keys(obj).map((label) => ({ label, value: obj[label] }));
}

export const roundDecimal = (number: number, precision?: number) => {
  if (Number.isInteger(number)) return number;
  else return parseFloat(number.toFixed(precision || 2));
};

export const getPlanName = (plan: PricingPlans, isTrial: boolean) =>
  `${PricingPlanNames[plan]}${isTrial ? ' Trial' : ''}`;

export const replacePricingPlanKeyword = (message: string) => {
  if (message.includes(PricingPlans.NO_ACTIVE_SUBSCRIPTION))
    return message.replace(
      PricingPlans.NO_ACTIVE_SUBSCRIPTION,
      PricingPlanNames[PricingPlans.NO_ACTIVE_SUBSCRIPTION]
    );
  if (message.includes(PricingPlans.PRO_PLUS))
    return message.replace(
      PricingPlans.PRO_PLUS,
      PricingPlanNames[PricingPlans.PRO_PLUS]
    );
  if (message.includes(PricingPlans.PRO))
    return message.replace(
      PricingPlans.PRO,
      PricingPlanNames[PricingPlans.PRO]
    );
  if (message.includes(PricingPlans.LITE))
    return message.replace(
      PricingPlans.LITE,
      PricingPlanNames[PricingPlans.LITE]
    );
  if (message.includes(PricingPlans.STARTER))
    return message.replace(
      PricingPlans.STARTER,
      PricingPlanNames[PricingPlans.STARTER]
    );

  return message;
};

export enum UploadLimits {
  ROWS = 1000,
  COLUMNS = 15
}

export const roundToNearestMagnitude = (number: number) => {
  const magnitudes = ['', 'K', 'M', 'B', 'T', 'P', 'E', 'Z', 'Y', 'R', 'Q'];

  if (number < 1000000) return number.toLocaleString();

  const magnitudeIndex = Math.floor(Math.log10(number) / 3);
  const magnitude = magnitudes[magnitudeIndex];

  if (magnitudeIndex < magnitudes.length) {
    const rounded = number / Math.pow(10, magnitudeIndex * 3);

    return `${
      Number.isInteger(rounded) ? rounded : rounded.toFixed(2)
    }${magnitude}`;
  } else {
    return number.toExponential(2);
  }
};

export const containsWhiteSpace = (str: string) => /\s/.test(str);

export const getFileType = (projectDataFiles: ProjectDataFile[]) => {
  for (const obj of projectDataFiles)
    if (obj.filetype?.toUpperCase() === 'GSHEET') return 'GSHEET';

  return projectDataFiles.length > 0 ? projectDataFiles[0].filetype : '';
};

export const getCurrentFilter = ({
  projectFilters,
  toggleDashboardFilter,
  chartGlobalFilterToggle,
  chartFilters
}: {
  projectFilters: boolean;
  toggleDashboardFilter: boolean;
  chartGlobalFilterToggle: boolean;
  chartFilters: boolean;
}) => {
  if (projectFilters)
    if (toggleDashboardFilter) return 'PROJECT';
    else if (chartGlobalFilterToggle && chartFilters) return 'CHART';
    else return 'NONE';
  else if (chartGlobalFilterToggle && chartFilters) return 'CHART';
  else return 'NONE';
};

export const getUTCTime = (date: string) =>
  dayjs(date).utc().format('ddd DD MMM YYYY hh:mm A UTC');
